import { useState, useEffect, useCallback, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Workbook } from '@fortune-sheet/react';
import '@fortune-sheet/react/dist/index.css';
import * as XLSX from 'xlsx';
import * as api from '../../api/analytics';
import { useSpreadsheetKeyboard } from '../../hooks/useSpreadsheetKeyboard';
import SOQLEditorModal from './SOQLEditorModal';
import ViewPickerDropdown from './ViewPickerDropdown';

// Insert a new field into a SOQL query's SELECT clause.
// Handles top-level FROM only (not inside subqueries/CTEs — those remain unchanged).
function addFieldToSoql(soql, objectName, fieldName) {
  // Walk through looking for top-level FROM keyword
  let depth = 0;
  for (let i = 0; i < soql.length; i++) {
    const ch = soql[i];
    if (ch === '(') { depth++; continue; }
    if (ch === ')') { depth--; continue; }
    if (depth > 0) continue;
    // Check for FROM keyword at top level
    if (soql.slice(i, i + 4).toUpperCase() === 'FROM') {
      const before = i === 0 || /\s/.test(soql[i - 1]);
      const after = i + 4 >= soql.length || /[\s(]/.test(soql[i + 4]);
      if (before && after) {
        // Insert field before the FROM keyword
        const selectPart = soql.slice(0, i).trimEnd();
        const fromPart = soql.slice(i);
        return `${selectPart}, ${fieldName}\n${fromPart}`;
      }
    }
  }
  return soql; // fallback: unchanged
}

const DEFAULT_SHEET_NAME = 'View Data';

// Cell background colors
const BG_DIRTY      = '#FFFDE7'; // yellow  — edited, not yet synced
const BG_READONLY   = '#F3F4F6'; // gray    — non-writable column
const BG_SYNC_FAIL  = '#FFEBEE'; // red     — sync failed for this row
const BG_AGGREGATE  = '#E8F5E9'; // green   — identity-less row (aggregate/no writeback)
const BG_ERROR      = '#FFEBEE'; // red     — validation error for this cell (highest priority)

// Range View highlight colors
const RANGE_COLORS = ['#4CAF50', '#2196F3', '#FF9800', '#9C27B0', '#F44336'];

// Parse a range reference like "B2:D10" into {startRow, startCol, endRow, endCol} (0-based)
function parseRangeRef(ref) {
  const match = ref.trim().match(/^([A-Z]+)(\d+):([A-Z]+)(\d+)$/i);
  if (!match) return null;
  const colToIdx = (col) => {
    let idx = 0;
    col = col.toUpperCase();
    for (let i = 0; i < col.length; i++) {
      idx = idx * 26 + (col.charCodeAt(i) - 64);
    }
    return idx - 1;
  };
  return {
    startRow: parseInt(match[2], 10) - 1,
    startCol: colToIdx(match[1]),
    endRow: parseInt(match[4], 10) - 1,
    endCol: colToIdx(match[3]),
  };
}

// Convert 0-based column index to letter(s) (0=A, 25=Z, 26=AA, ...)
function idxToCol(idx) {
  let col = '';
  let n = idx + 1;
  while (n > 0) {
    n--;
    col = String.fromCharCode(65 + (n % 26)) + col;
    n = Math.floor(n / 26);
  }
  return col;
}

export default function SpreadsheetView({ viewId, onClose, onSelectView }) {
  const [view, setView] = useState(null);
  const [sheetData, setSheetData] = useState(null);
  const [columnLineage, setColumnLineage] = useState([]);
  const [allColumnLineage, setAllColumnLineage] = useState([]);
  const [rowIdentities, setRowIdentities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncResults, setSyncResults] = useState(null);
  const [dirtyMap, setDirtyMap] = useState({}); // { "r:c": { old, new, row, col, colName, rowIdx } }
  const [failedRows, setFailedRows] = useState(new Set()); // rowIdx set for sync-failed rows
  const [originalRows, setOriginalRows] = useState([]);
  const [sheetState, setSheetState] = useState(null);
  const [showAddColumnDialog, setShowAddColumnDialog] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [syncHistory, setSyncHistory] = useState([]);
  const [revertingId, setRevertingId] = useState(null); // sync entry ID being reverted
  const [revertConflicts, setRevertConflicts] = useState(null);       // conflicts from last revert
  const [revertConflictEntryId, setRevertConflictEntryId] = useState(null); // which entry had conflicts
  const [rangeViews, setRangeViews] = useState([]);
  const [showRangeViewDialog, setShowRangeViewDialog] = useState(false);
  const [missingTables, setMissingTables] = useState([]); // objects in LEFT JOINs not yet synced
  const [isSyncingMissing, setIsSyncingMissing] = useState(false);
  const [identityLessRows, setIdentityLessRows] = useState(new Set()); // row indices with no row identity
  const [rowLMDates, setRowLMDates] = useState({}); // { sqlAlias: [lmdValuePerRow] }
  // alias_data_sources from QueryResponse: SQL alias → { data_source_id, supports_writeback }
  // Populated in loadData; used by handleSync for per-change routing (Phase 10A).
  const [aliasDataSources, setAliasDataSources] = useState({});
  const [showSOQLEditorModal, setShowSOQLEditorModal] = useState(false);
  const [currentOffset, setCurrentOffset] = useState(0);
  const [isFullyLoaded, setIsFullyLoaded] = useState(false);

  // Phase 8B — per-cell error display
  const [cellErrors, setCellErrors] = useState({}); // keyed by "${rowIdx}_${colIdx}"
  const [selectedCellError, setSelectedCellError] = useState(null);

  // Phase 8C — pre-sync validation
  const [validationErrors, setValidationErrors] = useState(null); // null=no run, []=passed

  // Real-time collaboration state (TASK-032 / TASK-033)
  const [connectedUsers, setConnectedUsers] = useState([]);
  const [wsStatus, setWsStatus] = useState('disconnected'); // disconnected | connecting | connected
  const workbookRef = useRef(null);
  const containerRef = useRef(null);
  useSpreadsheetKeyboard(containerRef, workbookRef);
  const wsRef = useRef(null);
  const clientSeqRef = useRef(0);
  const skipNextOpRef = useRef(false); // flag to skip broadcasting ops from remote apply
  // Always-current ref for dirtyMap — prevents stale closures in Fortune Sheet's onChange callback
  const dirtyMapRef = useRef({});
  // Always-current ref for sheetState — avoids stale closure in handleSaveState 409 retry
  const sheetStateRef = useRef(sheetState);
  // Always-current ref for cellErrors — prevents stale closures in Fortune Sheet's hooks
  const cellErrorsRef = useRef({});
  const user = useSelector((state) => state.auth.user);
  const activeTenant = useSelector((state) => state.auth.activeTenant);

  // Keep dirtyMapRef in sync with dirtyMap state (synchronous, always current)
  dirtyMapRef.current = dirtyMap;
  // Keep cellErrorsRef in sync with cellErrors state
  cellErrorsRef.current = cellErrors;

  // Keep sheetStateRef in sync so the 409 retry in handleSaveState reads the latest version
  useEffect(() => { sheetStateRef.current = sheetState; }, [sheetState]);

  // Test hook: allows Playwright tests to inject dirty state without canvas interaction.
  // Exposed in all non-production builds (DEV server or VITE_ENABLE_TEST_HOOKS=true).
  useEffect(() => {
    if (import.meta.env.DEV || import.meta.env.VITE_ENABLE_TEST_HOOKS === 'true') {
      window.__TEST_setDirty = (entries) => setDirtyMap(entries);
      return () => { delete window.__TEST_setDirty; };
    }
  }, []); // setDirtyMap is a stable React setter — no deps needed

  // Load view metadata, then data
  useEffect(() => {
    if (!viewId) return;
    api.fetchView(viewId).then(v => {
      setView(v);
      loadData(v);
    }).catch(e => setError(e.message));
  }, [viewId]);

  const loadData = async (currentView) => {
    setIsLoading(true);
    setError(null);
    setDirtyMap({});
    setFailedRows(new Set());
    setSyncResults(null);
    setCurrentOffset(0);
    setCellErrors({});
    setValidationErrors(null);
    try {
      // Load sheet state (column config, type3 columns)
      let state = null;
      try {
        state = await api.fetchSheetState(viewId);
      } catch (_) {
        state = null;
      }
      setSheetState(state);
      setRangeViews(state?.range_views || []);

      const isFreeMode = (currentView || view)?.sheet_mode === 'free';

      let result = { columns: [], rows: [], row_identities: [], column_lineage: [], missing_tables: [] };
      if (!isFreeMode) {
        // Execute view to get data with lineage + identities
        result = await api.executeViewForSpreadsheet(viewId, { limit: 1000, offset: 0 });
      }
      const all = result.column_lineage || [];
      setAllColumnLineage(all);
      setColumnLineage(all.filter(c => !c.hidden));
      setRowIdentities(result.row_identities || []);
      setOriginalRows(result.rows || []);
      setMissingTables(result.missing_tables || []);
      // Capture alias_data_sources for per-change datasource routing (Phase 10A).
      setAliasDataSources(result.alias_data_sources || {});
      setIsFullyLoaded(!result.truncated);
      setCurrentOffset(0);

      // Populate identityLessRows: indices where row_identity is an empty object
      const ilr = new Set(
        (result.row_identities || [])
          .map((id, idx) => Object.keys(id).length === 0 ? idx : -1)
          .filter(i => i >= 0)
      );
      setIdentityLessRows(ilr);

      // Extract hidden LMD column values for writeback conflict detection (Phase 2C)
      const lmdCols = (result.column_lineage || []).filter(c => c.hidden && c.name?.startsWith('__lmd_'));
      const lmdMap = {};
      lmdCols.forEach(col => {
        lmdMap[col.sql_alias] = (result.rows || []).map(row => row[col.name] ?? null);
      });
      setRowLMDates(lmdMap);

      // Convert to Fortune Sheet format
      const fortuneData = buildFortuneSheetData(result, state, {}, new Set(), ilr);
      setSheetData(fortuneData);
    } catch (err) {
      setError(err.message || 'Failed to load view data');
    } finally {
      setIsLoading(false);
    }
  };

  // WebSocket connection for real-time collaboration (TASK-032)
  const lastSeqRef = useRef(0);
  const reconnectAttemptRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const closedByUserRef = useRef(false);

  useEffect(() => {
    if (!viewId || isLoading) return;
    closedByUserRef.current = false;

    function connect() {
      const wsUrl = api.getWebSocketURL(viewId, activeTenant?.tenant_id);
      setWsStatus('connecting');
      const ws = new WebSocket(wsUrl, []);

      ws.onopen = () => {
        setWsStatus('connected');
        reconnectAttemptRef.current = 0;
        // Send join with last known seq for replay on reconnect
        ws.send(JSON.stringify({
          type: 'join',
          user_id: user?.id || 'anonymous',
          username: user?.display_name || user?.email || 'Anonymous',
          seq: lastSeqRef.current,
        }));
      };

      ws.onmessage = (event) => {
        let msg;
        try { msg = JSON.parse(event.data); } catch { return; }

        switch (msg.type) {
          case 'connected':
            setConnectedUsers(msg.users || []);
            if (msg.seq) lastSeqRef.current = msg.seq;
            break;

          case 'user_joined':
            setConnectedUsers(prev => {
              if (prev.some(u => u.user_id === msg.user_id)) return prev;
              return [...prev, { user_id: msg.user_id, username: msg.username, color: msg.color }];
            });
            break;

          case 'user_left':
            setConnectedUsers(prev => prev.filter(u => u.user_id !== msg.user_id));
            if (workbookRef.current?.removePresences) {
              workbookRef.current.removePresences([{ username: msg.username, userId: msg.user_id }]);
            }
            break;

          case 'replay':
            // Apply replayed ops sequentially on reconnect
            if (Array.isArray(msg.ops)) {
              msg.ops.forEach(entry => {
                if (entry.user_id !== user?.id && workbookRef.current?.applyOp) {
                  try {
                    skipNextOpRef.current = true;
                    workbookRef.current.applyOp(entry.ops);
                  } catch (e) {
                    console.warn('[WS] Failed to apply replayed op:', e);
                  }
                }
                if (entry.seq > lastSeqRef.current) lastSeqRef.current = entry.seq;
              });
            }
            break;

          case 'op_broadcast':
            if (msg.seq) lastSeqRef.current = msg.seq;
            if (msg.user_id !== user?.id && workbookRef.current?.applyOp) {
              try {
                skipNextOpRef.current = true;
                workbookRef.current.applyOp(msg.ops);
              } catch (e) {
                console.warn('[WS] Failed to apply remote op:', e);
              }
            }
            break;

          case 'cursor_broadcast':
            if (msg.user_id !== user?.id && workbookRef.current?.addPresences) {
              workbookRef.current.addPresences([{
                sheetId: msg.sheet_id || '0',
                username: msg.username,
                userId: msg.user_id,
                color: msg.color || '#4CAF50',
                selection: { r: msg.row ?? 0, c: msg.col ?? 0 },
              }]);
            }
            break;

          case 'pong':
            break; // keepalive acknowledged

          case 'error':
            console.warn('[WS] Server error:', msg.message);
            break;
        }
      };

      ws.onclose = () => {
        setWsStatus('disconnected');
        setConnectedUsers([]);
        wsRef.current = null;
        // Reconnect with exponential backoff (max 30s)
        if (!closedByUserRef.current) {
          const attempt = reconnectAttemptRef.current++;
          const delay = Math.min(1000 * Math.pow(2, attempt), 30000);
          reconnectTimerRef.current = setTimeout(connect, delay);
        }
      };

      ws.onerror = () => {
        // onclose will fire after onerror, triggering reconnect
      };

      wsRef.current = ws;
    }

    connect();

    return () => {
      closedByUserRef.current = true;
      clearTimeout(reconnectTimerRef.current);
      wsRef.current?.close();
      wsRef.current = null;
    };
  }, [viewId, isLoading, user?.id, activeTenant?.tenant_id]);

  // Send ops over WebSocket
  const sendOps = useCallback((ops) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'op_submit',
        ops,
        base_seq: lastSeqRef.current,
        client_seq: ++clientSeqRef.current,
      }));
    }
  }, []);

  // Send cursor position over WebSocket (TASK-033)
  const sendCursor = useCallback((row, col) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'cursor_update',
        row,
        col,
        sheet_id: '0',
      }));
    }
  }, []);

  // Determine background for a cell given dirty/failed/readonly/aggregate/error state
  // Precedence: BG_ERROR → BG_AGGREGATE → BG_SYNC_FAIL → BG_DIRTY → BG_READONLY
  const cellBg = (rowIdx, colIdx, colLineage, dirtyMapSnap, failedRowsSnap, identityLessRowsSnap, cellErrorsSnap = {}) => {
    if (cellErrorsSnap[`${rowIdx}_${colIdx}`]) return BG_ERROR; // highest priority
    if (identityLessRowsSnap && identityLessRowsSnap.has(rowIdx)) return BG_AGGREGATE;
    if (colLineage && !colLineage.writable) return BG_READONLY;
    if (failedRowsSnap.has(rowIdx)) return BG_SYNC_FAIL;
    if (dirtyMapSnap[`${rowIdx + 1}:${colIdx}`]) return BG_DIRTY;
    return undefined;
  };

  // Build Fortune Sheet workbook data from API response
  const buildFortuneSheetData = (result, state, dirtyMapSnap = {}, failedRowsSnap = new Set(), identityLessRowsSnap = new Set(), cellErrorsSnap = {}) => {
    const rows = result.rows || [];
    const columns = result.columns || [];
    const lineage = result.column_lineage || [];

    // Build column headers (always bold, no bg override)
    const headers = columns.map((colName, colIdx) => ({
      r: 0, c: colIdx,
      v: { v: colName, ct: { fa: 'General', t: 'g' }, bl: 1 }
    }));

    // Build data cells with read-only / dirty / failed / aggregate / error backgrounds
    const cells = [...headers];
    rows.forEach((row, rowIdx) => {
      columns.forEach((colName, colIdx) => {
        const lineageCol = lineage.find(l => l.name === colName);
        const value = row[colName];
        const bg = cellBg(rowIdx, colIdx, lineageCol, dirtyMapSnap, failedRowsSnap, identityLessRowsSnap, cellErrorsSnap);
        const cell = {
          r: rowIdx + 1,
          c: colIdx,
          v: { v: value, ct: { fa: 'General', t: inferCellType(value, lineageCol?.type) } }
        };
        if (bg) cell.v.bg = bg;
        cells.push(cell);
      });
    });

    // Add Type 3 columns — restore formulas and saved cell values
    const baseColCount = columns.length;
    if (state?.type3_columns?.length > 0) {
      state.type3_columns.forEach((t3col, t3Idx) => {
        const colIdx = baseColCount + t3Idx;
        cells.push({ r: 0, c: colIdx, v: { v: t3col.name, bl: 1, fc: '#1565C0' } });
        rows.forEach((_, rowIdx) => {
          if (t3col.formula) {
            cells.push({ r: rowIdx + 1, c: colIdx, v: { f: t3col.formula } });
          } else {
            // Restore previously saved cell value (type3_data[colId][rowIdx])
            const savedVal = state.type3_data?.[t3col.id]?.[String(rowIdx)];
            if (savedVal !== undefined && savedVal !== null) {
              cells.push({ r: rowIdx + 1, c: colIdx, v: { v: savedVal } });
            }
          }
        });
      });
    }

    // Column widths
    const colCount = baseColCount + (state?.type3_columns?.length || 0);
    const columnlen = {};
    for (let i = 0; i < colCount; i++) {
      const configEntry = state?.column_config?.find(cc => cc.position === i);
      columnlen[i] = configEntry?.width || 120;
    }

    // Picklist data validation — one entry per cell per picklist column
    const dataVerification = {};
    columns.forEach((colName, colIdx) => {
      const lineageCol = lineage.find(l => l.name === colName);
      if (lineageCol?.picklist_values?.length > 0) {
        const value1 = lineageCol.picklist_values.join(',');
        rows.forEach((_, rowIdx) => {
          dataVerification[`${rowIdx + 1}_${colIdx}`] = {
            type: 'list',
            value1,
            showDropDown: true,
            allowNullValue: true,
            prohibitInput: false, // allow typing; we enforce at pre-sync validation
            checked: false,
            hintShow: false,
            hintText: '',
            remote: false,
          };
        });
      }
    });

    // Build borderInfo for range view highlights
    const rvBorders = (state?.range_views || []).map((rv, idx) => ({
      rangeType: 'range',
      borderType: 'border-outside',
      style: '8',
      color: RANGE_COLORS[idx % RANGE_COLORS.length],
      range: [{ row: [rv.startRow, rv.endRow], column: [rv.startCol, rv.endCol] }],
    }));

    return [{
      name: DEFAULT_SHEET_NAME,
      celldata: cells,
      config: { columnlen, ...(rvBorders.length > 0 ? { borderInfo: rvBorders } : {}) },
      dataVerification,
    }];
  };

  const inferCellType = (value, fieldType) => {
    if (value === null || value === undefined) return 'g';
    if (fieldType === 'double' || fieldType === 'integer' || fieldType === 'currency' || fieldType === 'percent') return 'n';
    if (fieldType === 'date' || fieldType === 'datetime') return 'd';
    if (fieldType === 'boolean') return 'b';
    if (typeof value === 'number') return 'n';
    return 'g';
  };

  // Apply dirty/failed/readonly/aggregate/error styling to a celldata array.
  // Used both in onChange and after sync to keep visual state consistent.
  const applyStyles = useCallback((celldata, dirtyMapSnap, failedRowsSnap, identityLessRowsSnap, cellErrorsSnap = {}) => {
    return celldata.map(cell => {
      if (cell.r === 0) return cell;
      const lineageCol = columnLineage[cell.c];
      const bg = cellBg(cell.r - 1, cell.c, lineageCol, dirtyMapSnap, failedRowsSnap, identityLessRowsSnap, cellErrorsSnap);
      const currentBg = cell.v?.bg;
      if (bg !== currentBg) {
        if (bg) {
          return { ...cell, v: { ...(cell.v || {}), bg } };
        } else {
          const { bg: _removed, ...restV } = (cell.v || {});
          return { ...cell, v: restV };
        }
      }
      return cell;
    });
  }, [columnLineage]);

  // Save sheet state (layout, type3 columns, type3 cell data)
  const handleSaveState = async () => {
    try {
      // Extract Type 3 cell values from current sheet data
      // type3_data: { [colId]: { [rowIdx]: value } }
      const type3Data = {};
      const baseColCount = columnLineage.length;
      if (sheetStateRef.current?.type3_columns?.length > 0) {
        sheetStateRef.current.type3_columns.forEach((t3col, t3Idx) => {
          if (t3col.formula) return; // formula columns compute dynamically — don't save values
          const colIdx = baseColCount + t3Idx;
          const colData = {};
          (sheetData?.[0]?.celldata || []).forEach(cell => {
            if (cell.c === colIdx && cell.r > 0 && cell.v?.v !== null && cell.v?.v !== undefined) {
              colData[String(cell.r - 1)] = cell.v.v;
            }
          });
          if (Object.keys(colData).length > 0) {
            type3Data[t3col.id] = colData;
          }
        });
      }

      const currentState = {
        column_config: sheetStateRef.current?.column_config || [],
        type3_columns: sheetStateRef.current?.type3_columns || [],
        type3_data: type3Data,
        sort_config: sheetStateRef.current?.sort_config || [],
        filter_config: sheetStateRef.current?.filter_config || [],
        range_views: rangeViews,
        version: sheetStateRef.current?.version || 0,
      };
      const saved = await api.saveSheetState(viewId, currentState);
      setSheetState(saved);
      alert('Sheet state saved!');
    } catch (err) {
      if (err.status === 409 || err.message?.includes('version_conflict')) {
        // Snapshot dirty cell edits AND local layout state before reload.
        const cellSnapshot = { ...dirtyMap };
        const localColumnConfig = sheetStateRef.current?.column_config || [];
        const localType3Columns = sheetStateRef.current?.type3_columns || [];
        const localSortConfig = sheetStateRef.current?.sort_config || [];
        const localFilterConfig = sheetStateRef.current?.filter_config || [];
        const localRangeViews = rangeViews;

        // Snapshot type3_data NOW, before loadData resets sheetData.
        const localType3Data = {};
        const baseColCount = columnLineage.length;
        if (localType3Columns.length > 0) {
          localType3Columns.forEach((t3col, t3Idx) => {
            if (t3col.formula) return;
            const colIdx = baseColCount + t3Idx;
            const colData = {};
            (sheetData?.[0]?.celldata || []).forEach(cell => {
              if (cell.c === colIdx && cell.r > 0 && cell.v?.v != null) {
                colData[String(cell.r - 1)] = cell.v.v;
              }
            });
            if (Object.keys(colData).length > 0) localType3Data[t3col.id] = colData;
          });
        }

        // loadData clears dirtyMap and calls setSheetState with server version.
        await loadData(view);
        // After loadData, sheetStateRef.current has the new server state + version.
        setDirtyMap(cellSnapshot);
        setSheetState(prev => ({
          ...prev,
          column_config: localColumnConfig,
          type3_columns: localType3Columns,
          sort_config: localSortConfig,
          filter_config: localFilterConfig,
        }));
        setRangeViews(localRangeViews);
        // Inject snapshotted type3 cell values back into sheetData so handleSaveState
        // re-extracts them correctly on retry.
        if (Object.keys(localType3Data).length > 0) {
          setSheetData(prev => {
            const updated = JSON.parse(JSON.stringify(prev)); // deep copy
            const cells = updated?.[0]?.celldata || [];
            localType3Columns.forEach((t3col, t3Idx) => {
              const colIdx = baseColCount + t3Idx;
              const colData = localType3Data[t3col.id] || {};
              Object.entries(colData).forEach(([rowIdxStr, val]) => {
                const r = parseInt(rowIdxStr, 10) + 1;
                const existing = cells.find(c => c.r === r && c.c === colIdx);
                if (existing) { existing.v = { ...existing.v, v: val }; }
                else { cells.push({ r, c: colIdx, v: { v: val } }); }
              });
            });
            return updated;
          });
        }
        await new Promise(resolve => setTimeout(resolve, 0));
        await handleSaveState();
      } else {
        alert('Failed to save: ' + err.message);
      }
    }
  };

  // Determine if a dirty cell belongs to a promoted range (for range writeback).
  // Returns the matching promoted range view or null if it's a Salesforce cell.
  const findPromotedRangeForCell = useCallback((change) => {
    const lineageCol = columnLineage[change.col];
    if (lineageCol?.is_range && lineageCol.range_name) {
      return rangeViews.find(r => r.is_promoted && r.name === lineageCol.range_name) || null;
    }
    return null;
  }, [columnLineage, rangeViews]);

  // Phase 8C — Pre-sync validation: checks required, max_length, picklist constraints
  function validateChangesBeforeSync(changes, colLineage) {
    const valid = [];
    const invalid = [];
    for (const change of changes) {
      const col = colLineage[change.col];
      if (!col || col.is_range || col.hidden) { valid.push(change); continue; }

      const val = change.new;
      let error = null;

      if (col.required && (val == null || val === '')) {
        error = `${col.name} is required`;
      } else if (col.max_length > 0 && typeof val === 'string' && val.length > col.max_length) {
        error = `${col.name} exceeds max length of ${col.max_length}`;
      } else if (col.picklist_values?.length > 0 && val != null && val !== '' && !col.picklist_values.includes(String(val))) {
        error = `${col.name}: "${val}" is not a valid picklist value`;
      }

      if (error) { invalid.push({ ...change, error }); }
      else { valid.push(change); }
    }
    return { valid, invalid };
  }

  // Sync dirty cells — splits into Salesforce writeback and range writeback
  const handleSync = async () => {
    const changes = Object.values(dirtyMap);
    if (changes.length === 0) {
      alert('No changes to sync.');
      return;
    }

    // Split changes into range edits vs Salesforce edits
    const sfChanges = [];
    const rangeEdits = [];
    for (const change of changes) {
      const promotedRange = findPromotedRangeForCell(change);
      if (promotedRange) {
        const lineageCol = columnLineage[change.col];
        // Calculate row index relative to the range's data rows (header row excluded)
        // change.rowIdx is the 0-based row in the query result; the promoted range rows
        // are a separate snapshot, so we use rowIdx directly as the range row index.
        rangeEdits.push({
          range_name: promotedRange.name,
          row_index: change.rowIdx,
          col_name: lineageCol?.name || change.colName,
          new_value: change.new,
          _dirtyKey: `${change.row}:${change.col}`, // track for cleanup
        });
      } else {
        sfChanges.push(change);
      }
    }

    // Client-side pre-sync validation for Salesforce cells (Phase 8C)
    const { invalid } = validateChangesBeforeSync(sfChanges, columnLineage);
    if (invalid.length > 0) {
      const newCellErrors = {};
      invalid.forEach(change => {
        newCellErrors[`${change.rowIdx}_${change.col}`] = change.error;
      });
      setCellErrors(newCellErrors);
      setValidationErrors(invalid);
      return;
    }
    // Clear any previous validation errors now that validation passed
    setCellErrors({});
    setValidationErrors([]);

    setIsSyncing(true);
    setSyncResults(null);
    try {
      let sfResult = null;
      let rangeResult = null;
      const newFailedRows = new Set();
      let newDirtyMap = { ...dirtyMap };

      // 1. CRM writeback — multi-source routing via per-change data_source_id (Phase 10A)
      if (sfChanges.length > 0) {
        // Legacy fallback: single datasource ID from the first sourced column.
        // Used as req.data_source_id for backward compat with pre-Phase-2C backends.
        const dataSourceId = columnLineage.find(c => c.source)?.source || '';
        const changeRequests = sfChanges
          .filter(change => {
            // Drop changes to non-writeback data sources (Phase 2C)
            const lineage = columnLineage[change.col];
            if (!lineage?.source) return false;
            const dsProfile = aliasDataSources[lineage.sql_alias];
            return dsProfile?.supports_writeback !== false;
          })
          .map(change => {
            const lineageCol = columnLineage[change.col];
            const rowIdentity = rowIdentities[change.rowIdx] || {};
            const sqlAlias = lineageCol?.sql_alias || '';
            return {
              row_identity: rowIdentity,
              object: lineageCol?.object || '',
              field: lineageCol?.field || change.colName,
              old_value: change.old,
              new_value: change.new,
              sql_alias: sqlAlias,                                          // Phase 10A
              data_source_id: lineageCol?.source || '',
              last_modified_date: rowLMDates[sqlAlias]?.[change.rowIdx] || null,
            };
          }).filter(c => c.object && Object.keys(c.row_identity).length > 0);

        // Build alias_data_sources: SQL alias → DataSourceID for server-side routing.
        // Derived from aliasDataSources state (populated from QueryResponse.alias_data_sources).
        const aliasDSForPayload = {};
        for (const [alias, info] of Object.entries(aliasDataSources)) {
          if (info?.data_source_id) {
            aliasDSForPayload[alias] = info.data_source_id;
          }
        }

        sfResult = await api.syncView(viewId, {
          data_source_id: dataSourceId,
          changes: changeRequests,
          alias_data_sources: Object.keys(aliasDSForPayload).length > 0 ? aliasDSForPayload : undefined,
        });

        // Determine which rows failed
        (sfResult.results || []).forEach(r => {
          if (!r.success) {
            rowIdentities.forEach((identity, rowIdx) => {
              if (identity[r.object] === r.record_id) {
                newFailedRows.add(rowIdx);
              }
            });
          }
        });

        // Clear dirty map for SF cells whose rows succeeded
        const cleanDirtyMap = {};
        for (const [key, change] of Object.entries(newDirtyMap)) {
          if (findPromotedRangeForCell(change)) {
            cleanDirtyMap[key] = change; // range cells handled below
          } else if (newFailedRows.has(change.rowIdx)) {
            cleanDirtyMap[key] = change; // keep failed SF rows dirty
          }
        }
        newDirtyMap = cleanDirtyMap;
      }

      // 2. Range writeback
      if (rangeEdits.length > 0) {
        rangeResult = await api.syncRangeEdits(viewId, rangeEdits.map(e => ({
          range_name: e.range_name,
          row_index: e.row_index,
          col_name: e.col_name,
          new_value: e.new_value,
        })));

        // Clear dirty map for successfully synced range cells
        if (rangeResult && (rangeResult.errors || []).length === 0) {
          // All range edits succeeded — remove them from dirty map
          const rangeKeys = new Set(rangeEdits.map(e => e._dirtyKey));
          const cleanDirtyMap = {};
          for (const [key, change] of Object.entries(newDirtyMap)) {
            if (!rangeKeys.has(key)) {
              cleanDirtyMap[key] = change;
            }
          }
          newDirtyMap = cleanDirtyMap;
        }
      }

      // Build combined sync results display
      const combinedResult = {
        success_count: (sfResult?.success_count || 0) + (rangeResult?.updated || 0),
        failure_count: (sfResult?.failure_count || 0) + (rangeResult?.errors?.length || 0),
        results: sfResult?.results || [],
        range_updated: rangeResult?.updated || 0,
      };
      setSyncResults(combinedResult);
      setFailedRows(newFailedRows);
      setDirtyMap(newDirtyMap);

      // Re-apply styles to reflect the new state
      if (sheetData?.[0]) {
        const updatedCells = applyStyles(sheetData[0].celldata || [], newDirtyMap, newFailedRows, identityLessRows);
        setSheetData([{ ...sheetData[0], celldata: updatedCells }]);
      }
    } catch (err) {
      alert('Sync failed: ' + err.message);
    } finally {
      setIsSyncing(false);
    }
  };

  // Export to CSV
  const handleExportCSV = () => {
    if (!originalRows.length || !columnLineage.length) return;
    const cols = columnLineage.map(c => c.name);
    const csvRows = [cols.join(',')];
    originalRows.forEach(row => {
      csvRows.push(cols.map(col => {
        const val = row[col] ?? '';
        const str = String(val);
        return str.includes(',') || str.includes('"') ? `"${str.replace(/"/g, '""')}"` : str;
      }).join(','));
    });
    const blob = new Blob([csvRows.join('\n')], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${view?.name || 'export'}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Export to XLSX (uses celldata for formula-evaluated values + Type 3 columns)
  const handleExportXLSX = () => {
    const celldata = sheetData?.[0]?.celldata;
    if (!celldata || celldata.length === 0) return;

    // Build 2D array from celldata
    let maxRow = 0, maxCol = 0;
    celldata.forEach(cell => {
      if (cell.r > maxRow) maxRow = cell.r;
      if (cell.c > maxCol) maxCol = cell.c;
    });

    const aoa = [];
    for (let r = 0; r <= maxRow; r++) {
      aoa.push(new Array(maxCol + 1).fill(''));
    }
    celldata.forEach(cell => {
      aoa[cell.r][cell.c] = cell.v?.v ?? '';
    });

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    XLSX.utils.book_append_sheet(wb, ws, (view?.name || 'Sheet1').slice(0, 31));
    XLSX.writeFile(wb, `${view?.name || 'export'}.xlsx`);
  };

  // Add Type 1 column — updates the FrankenQL, saves view, reloads data
  const handleAddType1Column = async (objectName, fieldApiName) => {
    try {
      const currentSoql = view?.soql_content || '';
      if (!currentSoql) {
        alert('Cannot modify view: SOQL content not loaded.');
        return;
      }
      const newSoql = addFieldToSoql(currentSoql, objectName, fieldApiName);
      if (newSoql === currentSoql) {
        alert('Could not locate the FROM clause to insert the field.');
        return;
      }
      // Save updated SOQL — backend re-runs parser and updates lineage
      await api.updateView(viewId, { soql_content: newSoql });
      // Reload data with new column
      await loadData();
      // Refresh view metadata (name/description may not change but soql_content will)
      const updatedView = await api.fetchView(viewId);
      setView(updatedView);
    } catch (err) {
      alert('Failed to add column: ' + err.message);
    }
  };

  // Add Type 2 column — creates field in Salesforce.
  // The field won't appear in DuckDB until the next data sync, so we don't auto-add to SOQL.
  // The dialog displays success instructions instead.
  const handleAddType2Column = async (objectName, fieldDef) => {
    const result = await api.createCustomField(objectName, fieldDef);
    return result; // caller shows success message and closes
  };

  // Add Type 3 column
  const handleAddType3Column = (colDef) => {
    const newCol = {
      id: crypto.randomUUID(),
      name: colDef.name,
      type: colDef.type,
      formula: colDef.formula || '',
      position: (sheetState?.type3_columns?.length || 0),
    };
    const updated = {
      ...sheetState,
      type3_columns: [...(sheetState?.type3_columns || []), newCol],
    };
    setSheetState(updated);

    if (sheetData) {
      const newSheetData = [...sheetData];
      const sheet = { ...newSheetData[0] };
      const colIdx = columnLineage.length + (updated.type3_columns.length - 1);
      const newCells = [...(sheet.celldata || [])];
      newCells.push({ r: 0, c: colIdx, v: { v: newCol.name, bl: 1, fc: '#1565C0' } });
      if (newCol.formula) {
        for (let rowIdx = 1; rowIdx <= originalRows.length; rowIdx++) {
          newCells.push({ r: rowIdx, c: colIdx, v: { f: newCol.formula } });
        }
      }
      newSheetData[0] = { ...sheet, celldata: newCells };
      setSheetData(newSheetData);
    }
    setShowAddColumnDialog(false);
  };

  // Load more rows (pagination)
  const handleLoadMore = async () => {
    const existingRowCount = originalRows.length;
    const nextOffset = existingRowCount;
    try {
      const nextResult = await api.executeViewForSpreadsheet(viewId, { limit: 1000, offset: nextOffset });
      const newRows = nextResult.rows || [];
      const newIdentities = nextResult.row_identities || [];
      const columns = nextResult.columns || [];
      const lineage = nextResult.column_lineage || columnLineage;

      // Append to data arrays
      setOriginalRows(prev => [...prev, ...newRows]);
      setRowIdentities(prev => [...prev, ...newIdentities]);
      setCurrentOffset(nextOffset + newRows.length);
      setIsFullyLoaded(!nextResult.truncated);

      // Build new cells inline (replicate inner row-building loop from buildFortuneSheetData)
      setSheetData(prev => {
        if (!prev?.[0]) return prev;
        const updated = JSON.parse(JSON.stringify(prev));
        const cells = updated[0].celldata || [];
        const startRowIdx = existingRowCount;

        newRows.forEach((row, idx) => {
          const rowIdx = startRowIdx + idx;
          columns.forEach((colName, colIdx) => {
            const lineageCol = lineage.find(l => l.name === colName);
            const value = row[colName];
            const bg = cellBg(rowIdx, colIdx, lineageCol, dirtyMapRef.current, failedRows);
            const cell = {
              r: rowIdx + 1,
              c: colIdx,
              v: { v: value, ct: { fa: 'General', t: inferCellType(value, lineageCol?.type) } }
            };
            if (bg) cell.v.bg = bg;
            cells.push(cell);
          });

          // Type 3 columns for new rows
          if (sheetStateRef.current?.type3_columns?.length > 0) {
            const baseColCount = columns.length;
            sheetStateRef.current.type3_columns.forEach((t3col, t3Idx) => {
              const colIdx = baseColCount + t3Idx;
              if (t3col.formula) {
                cells.push({ r: rowIdx + 1, c: colIdx, v: { f: t3col.formula } });
              }
              // Static type3 cells for new rows have no saved value — leave empty
            });
          }
        });

        updated[0] = { ...updated[0], celldata: cells };
        return updated;
      });
    } catch (err) {
      alert('Failed to load more rows: ' + err.message);
    }
  };

  const dirtyCount = Object.keys(dirtyMap).length;
  // effectiveDirtyCount excludes identity-less rows (those cannot be synced)
  const effectiveDirtyCount = Object.keys(dirtyMap).filter(key => {
    const rowIdx = parseInt(key.split(':')[0]) - 1; // key is "row:col" (1-based row)
    return !identityLessRows.has(rowIdx);
  }).length;

  // Warn before browser close/refresh with unsaved changes
  useEffect(() => {
    if (dirtyCount === 0) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirtyCount]);

  const handleClose = () => {
    if (dirtyCount > 0 && !window.confirm(`You have ${dirtyCount} unsaved change${dirtyCount !== 1 ? 's' : ''}. Leave without syncing to Salesforce?`)) {
      return;
    }
    onClose();
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-lg text-gray-600">Loading spreadsheet...</div>
      </div>
    );
  }

  if (error) {
    // "table Task not synced for tenant default-tenant" → show actionable sync UI
    const notSyncedMatch = error.match(/^table\s+(\S+)\s+not synced/i);
    if (notSyncedMatch) {
      const tableName = notSyncedMatch[1];
      return (
        <div className="flex flex-col items-center justify-center h-full p-8" data-testid="missing-table-error">
          <div className="max-w-lg w-full bg-white border border-amber-200 rounded-xl shadow-sm p-8 flex flex-col items-center gap-5">

            {/* Icon */}
            <div className="w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center">
              <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>

            {/* Heading */}
            <div className="text-center">
              <h2 className="text-xl font-semibold text-gray-900 mb-1" data-testid="missing-table-heading">
                {view?.name || 'This view'} needs data to be synced
              </h2>
              <p className="text-gray-500 text-sm">
                This spreadsheet cannot display any data until the required object is synced from your CRM.
              </p>
            </div>

            {/* Missing table pill */}
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 w-full" data-testid="missing-table-pill">
              <div className="w-2.5 h-2.5 rounded-full bg-amber-500 flex-shrink-0 mt-1" />
              <div>
                <p className="text-sm font-semibold text-amber-900">{tableName}</p>
                <p className="text-xs text-amber-700 mt-0.5">
                  This object is used in this view but has not been synced yet. All rows in the view depend on it.
                </p>
              </div>
            </div>

            {/* What to do */}
            <p className="text-xs text-gray-400 text-center leading-relaxed">
              Click <span className="font-medium text-gray-600">Sync {tableName} now</span> to start
              a sync. Once it completes, reload this page to see your data. You can track sync
              progress on the <span className="font-medium text-gray-600">Sync Config</span> tab.
            </p>

            {/* Actions */}
            <div className="flex gap-3 w-full pt-1">
              <button
                onClick={handleClose}
                className="flex-1 px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                data-testid="back-to-views-btn"
              >
                ← Back to views
              </button>
              <button
                onClick={async () => {
                  setIsSyncingMissing(true);
                  try {
                    await api.triggerSyncNow();
                  } finally {
                    setIsSyncingMissing(false);
                  }
                }}
                disabled={isSyncingMissing}
                className="flex-1 px-4 py-2 text-sm font-medium bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50"
                data-testid="sync-missing-btn"
              >
                {isSyncingMissing ? 'Starting sync…' : `Sync ${tableName} now`}
              </button>
            </div>

          </div>
        </div>
      );
    }

    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-red-500">{error}</div>
      </div>
    );
  }

  const isFreeMode = view?.sheet_mode === 'free';

  return (
    <div className="flex flex-col h-full" data-testid="spreadsheet-view">
      {/* Toolbar */}
      <div className="flex items-center gap-2 px-4 py-2 bg-white border-b shadow-sm" data-testid="spreadsheet-toolbar">
        <button onClick={handleClose} className="text-gray-500 hover:text-gray-700 mr-2">
          &larr; Back
        </button>
        <h1 className="text-lg font-semibold" data-testid="spreadsheet-title">
          {view?.name || 'Spreadsheet View'}
          {isFreeMode && (
            <span className="ml-2 text-xs font-normal text-purple-600 bg-purple-50 px-2 py-0.5 rounded" data-testid="free-mode-badge">Free Sheet</span>
          )}
        </h1>

        {isFreeMode && (
          <button onClick={() => setShowSOQLEditorModal(true)}
            className="px-3 py-1 text-sm bg-purple-50 text-purple-700 rounded hover:bg-purple-100"
            data-testid="add-query-btn">
            + Add query
          </button>
        )}

        {/* Phase 8A — View picker dropdown */}
        {onSelectView && (
          <ViewPickerDropdown
            currentViewId={viewId}
            currentViewName={view?.name}
            onSelect={onSelectView}
          />
        )}

        {/* Spacer */}
        <div className="flex-1" />


        {/* Connected users + WebSocket status indicator (TASK-033) */}
        <div className="flex items-center gap-1 mr-1" data-testid="connected-users">
          {connectedUsers.slice(0, 5).map((u) => (
            <span
              key={u.user_id}
              className="inline-flex items-center justify-center w-6 h-6 rounded-full text-white text-xs font-medium"
              style={{ background: u.color || '#9E9E9E' }}
              title={u.username || u.user_id}
            >
              {(u.username || u.user_id || '?')[0].toUpperCase()}
            </span>
          ))}
          {connectedUsers.length > 5 && (
            <span className="text-xs text-gray-500">+{connectedUsers.length - 5}</span>
          )}
          <span
            className={`ml-1 inline-block w-2 h-2 rounded-full ${wsStatus === 'connected' ? 'bg-green-500' : wsStatus === 'connecting' ? 'bg-yellow-400' : 'bg-gray-300'}`}
            title={`WebSocket: ${wsStatus}`}
            data-testid="ws-status"
            data-ws-status={wsStatus}
          />
        </div>

        {dirtyCount > 0 && (
          <span className="text-sm text-amber-600 bg-amber-50 px-2 py-1 rounded" data-testid="dirty-indicator">
            {dirtyCount} unsaved change{dirtyCount !== 1 ? 's' : ''}
          </span>
        )}

        <button
          onClick={() => setShowAddColumnDialog(true)}
          className="px-3 py-1 text-sm bg-blue-50 text-blue-700 rounded hover:bg-blue-100"
          data-testid="add-column-btn"
        >
          + Column
        </button>

        <button
          onClick={handleExportCSV}
          className="px-3 py-1 text-sm bg-gray-50 text-gray-700 rounded hover:bg-gray-100"
          data-testid="export-btn"
        >
          Export CSV
        </button>

        <button
          onClick={handleExportXLSX}
          className="px-3 py-1 text-sm bg-gray-50 text-gray-700 rounded hover:bg-gray-100"
          data-testid="export-xlsx-btn"
        >
          Export XLSX
        </button>

        {!isFullyLoaded && !isFreeMode && (
          <button onClick={handleLoadMore}
            className="px-3 py-1 text-sm bg-gray-50 border border-gray-200 text-gray-700 rounded hover:bg-gray-100"
            data-testid="load-more-btn">
            Load more
          </button>
        )}

        {!isFreeMode && (
          <button
            onClick={() => setShowRangeViewDialog(true)}
            disabled={!isFullyLoaded}
            className={`px-3 py-1 text-sm rounded hover:bg-indigo-100 ${!isFullyLoaded ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : 'bg-indigo-50 text-indigo-700'}`}
            data-testid="define-range-btn"
            title={!isFullyLoaded ? 'Load all rows before promoting a range' : undefined}
          >
            Range Views{rangeViews.length > 0 ? ` (${rangeViews.length})` : ''}
          </button>
        )}

        {!isFreeMode && (
          <button
            onClick={() => {
              api.fetchSyncHistory(viewId)
                .then(h => { setSyncHistory(h || []); setShowHistory(true); })
                .catch(() => { setSyncHistory([]); setShowHistory(true); });
            }}
            className="px-3 py-1 text-sm bg-gray-50 text-gray-700 rounded hover:bg-gray-100"
            data-testid="history-btn"
          >
            History
          </button>
        )}

        <button
          onClick={handleSaveState}
          className="px-3 py-1 text-sm bg-green-50 text-green-700 rounded hover:bg-green-100"
          data-testid="save-state-btn"
        >
          Save Layout
        </button>

        {!isFreeMode && (
          <button
            onClick={handleSync}
            disabled={isSyncing || effectiveDirtyCount === 0}
            className={`px-3 py-1 text-sm rounded font-medium ${
              effectiveDirtyCount > 0
                ? 'bg-blue-600 text-white hover:bg-blue-700'
                : 'bg-gray-200 text-gray-500 cursor-not-allowed'
            }`}
            data-testid="sync-btn"
          >
            {isSyncing ? 'Syncing...' : `Sync Changes${effectiveDirtyCount > 0 ? ` (${effectiveDirtyCount})` : ''}`}
          </button>
        )}
      </div>

      {/* Missing tables banner — shown when LEFT JOIN objects haven't been synced */}
      {missingTables.length > 0 && (
        <div
          className="flex items-center gap-3 px-4 py-2 bg-amber-50 border-b border-amber-200 text-sm text-amber-800"
          data-testid="missing-tables-banner"
        >
          <span className="font-medium">Partial results:</span>
          <span>
            {missingTables.join(', ')} {missingTables.length === 1 ? 'hasn\'t' : 'haven\'t'} been synced yet.
            This view shows data without those objects.
          </span>
          <button
            onClick={async () => {
              setIsSyncingMissing(true);
              try {
                await api.triggerSyncNow();
              } finally {
                setIsSyncingMissing(false);
              }
            }}
            disabled={isSyncingMissing}
            className="ml-auto px-3 py-1 text-xs font-medium bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50"
            data-testid="sync-missing-btn"
          >
            {isSyncingMissing ? 'Starting sync…' : 'Sync missing objects'}
          </button>
        </div>
      )}

      {/* Identity-less rows banner — shown when some rows have no row identity (e.g. aggregate queries) */}
      {identityLessRows.size > 0 && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 text-sm px-3 py-2 rounded mx-4 mb-2">
          {identityLessRows.size} row(s) have no row identity and cannot be synced.
          Edits to these rows are blocked.
        </div>
      )}

      {/* Sync results banner */}
      {syncResults && (
        <div
          className={`px-4 py-2 text-sm ${syncResults.failure_count > 0 ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}
          data-testid="sync-results"
        >
          Sync complete: {syncResults.success_count} succeeded, {syncResults.failure_count} failed.
          {syncResults.range_updated > 0 && (
            <span className="ml-2 text-indigo-600">({syncResults.range_updated} range cell{syncResults.range_updated !== 1 ? 's' : ''} updated)</span>
          )}
          {(syncResults.results || []).filter(r => !r.success).map((r, i) => (
            <div key={i} className="ml-4 text-xs" data-testid="sync-error-detail">
              {r.object} {r.record_id}: {r.error}
            </div>
          ))}
        </div>
      )}

      {/* Phase 8C — Validation error banner */}
      {validationErrors?.length > 0 && (
        <div
          className="bg-red-50 border-b border-red-200 text-red-800 text-sm px-3 py-2 flex items-center gap-2"
          data-testid="validation-errors-banner"
        >
          <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          <span>{validationErrors.length} field{validationErrors.length !== 1 ? 's' : ''} have validation errors. Fix them before syncing.</span>
          <button
            onClick={() => { setValidationErrors(null); setCellErrors({}); }}
            className="ml-auto text-red-600 hover:text-red-800 text-xs underline"
          >
            Dismiss
          </button>
        </div>
      )}

      {/* Phase 8B — Selected cell error tooltip */}
      {selectedCellError && (
        <div
          className="bg-red-50 border-b border-red-200 text-red-700 text-sm px-3 py-1 flex items-center gap-2"
          data-testid="cell-error-tooltip"
        >
          <svg className="w-3 h-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 4a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1h-1z" clipRule="evenodd" />
          </svg>
          {selectedCellError}
        </div>
      )}

      {/* Fortune Sheet */}
      <div
        ref={containerRef}
        className="flex-1 overflow-hidden"
        data-testid="fortune-sheet-container"
      >
        {sheetData && (
          <Workbook
            ref={workbookRef}
            data={sheetData}
            onOp={(ops) => {
              // Broadcast local ops to server (TASK-032)
              if (skipNextOpRef.current) {
                skipNextOpRef.current = false;
                return;
              }
              sendOps(ops);
            }}
            onChange={(data) => {
              if (!data?.[0]) return;
              const newCells = data[0].celldata || [];
              const oldCells = sheetData[0]?.celldata || [];

              // Fortune Sheet sometimes fires onChange spuriously (e.g. after styling
              // updates or internal re-renders). Skip if no actual cell values changed —
              // this prevents the handler from stomping on test-injected dirty state or
              // triggering unnecessary re-renders.
              const hasValueChange = newCells.some(cell => {
                if (cell.r === 0) return false; // header row never dirty
                const old = oldCells.find(c => c.r === cell.r && c.c === cell.c);
                return (cell.v?.v ?? '') !== (old?.v?.v ?? '');
              });
              if (!hasValueChange) return;

              // Compute the new dirty map from scratch based on changed cells.
              // Use dirtyMapRef.current (not dirtyMap from closure) to avoid stale state
              // when Fortune Sheet holds an old onChange reference across renders.
              const newDirtyMap = { ...dirtyMapRef.current };

              newCells.forEach(cell => {
                if (cell.r === 0) return; // header row
                const lineageCol = columnLineage[cell.c];
                const rowIdx = cell.r - 1;

                // Block edits on identity-less rows (aggregate rows, no writeback possible)
                if (identityLessRows.has(rowIdx)) {
                  const originalValue = originalRows[rowIdx]?.[lineageCol?.name];
                  cell.v = { ...(cell.v || {}), v: originalValue, bg: BG_AGGREGATE };
                  return;
                }

                // Block edits on partial-identity LEFT JOIN columns (object not in row identity)
                const rowId = rowIdentities[rowIdx] || {};
                const colObject = lineageCol?.object;
                if (colObject && lineageCol?.writable && !lineageCol?.is_range) {
                  if (!Object.prototype.hasOwnProperty.call(rowId, colObject)) {
                    const originalValue = originalRows[rowIdx]?.[lineageCol?.name];
                    cell.v = { ...(cell.v || {}), v: originalValue, bg: BG_AGGREGATE };
                    return;
                  }
                }

                // Revert writes to non-writable columns
                if (lineageCol && !lineageCol.writable) {
                  const originalValue = originalRows[cell.r - 1]?.[lineageCol.name];
                  cell.v = { ...(cell.v || {}), v: originalValue, bg: BG_READONLY };
                  return;
                }

                // Check if value changed (ignore bg in comparison)
                const oldCell = oldCells.find(c => c.r === cell.r && c.c === cell.c);
                const oldValue = oldCell?.v?.v;
                const newValue = cell.v?.v;

                if (newValue !== oldValue) {
                  const key = `${cell.r}:${cell.c}`;
                  const colName = lineageCol?.name || `col_${cell.c}`;
                  const originalValue = originalRows[rowIdx]?.[colName];

                  if (newValue !== originalValue) {
                    newDirtyMap[key] = {
                      old: originalValue,
                      new: newValue,
                      row: cell.r,
                      col: cell.c,
                      colName,
                      rowIdx,
                      sqlAlias: lineageCol?.sql_alias || '',
                    };
                  } else {
                    // Reverted to original — no longer dirty
                    delete newDirtyMap[key];
                  }
                }
              });

              setDirtyMap(newDirtyMap);

              // Apply dirty / readonly / failed / aggregate backgrounds
              const styledCells = applyStyles(newCells, newDirtyMap, failedRows, identityLessRows);
              setSheetData([{ ...data[0], celldata: styledCells }]);
            }}
            hooks={{
              afterSelectionChange: (_sheetId, selection) => {
                // Broadcast cursor position to collaborators (TASK-033)
                if (selection) {
                  sendCursor(selection.row?.[0] ?? 0, selection.column?.[0] ?? 0);
                }
                // Phase 8B — show per-cell error for selected cell
                if (selection) {
                  const rowIdx = (selection.row?.[0] ?? 0) - 1; // row 0 is header
                  const colIdx = selection.column?.[0] ?? 0;
                  const err = cellErrorsRef.current[`${rowIdx}_${colIdx}`] || null;
                  setSelectedCellError(err);
                }
              },
            }}
            showToolbar={true}
            showFormulaBar={true}
            showSheetTabs={false}
            style={{ width: '100%', height: '100%' }}
          />
        )}
      </div>

      {/* Legend + stats */}
      <div className="px-4 py-1 bg-gray-50 border-t text-xs text-gray-500 flex gap-4 items-center" data-testid="lineage-panel">
        {isFreeMode ? (
          <span>Free Sheet — no data source. Edits are not synced anywhere.</span>
        ) : (
          <>
            <span className="flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded" style={{ background: BG_DIRTY }} />
              Edited
            </span>
            <span className="flex items-center gap-1">
              <span className="inline-block w-3 h-3 rounded" style={{ background: BG_READONLY }} />
              Read-only
            </span>
            {failedRows.size > 0 && (
              <span className="flex items-center gap-1">
                <span className="inline-block w-3 h-3 rounded" style={{ background: BG_SYNC_FAIL }} />
                Sync failed
              </span>
            )}
            <span className="ml-4" data-testid="lineage-writable-count">{columnLineage.filter(c => c.writable).length} editable columns</span>
            <span data-testid="lineage-readonly-count">{columnLineage.filter(c => !c.writable).length} read-only columns</span>
            <span data-testid="lineage-row-count">{originalRows.length} rows</span>
          </>
        )}
      </div>

      {/* Sync History Panel */}
      {showHistory && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" data-testid="history-panel">
          <div className="bg-white rounded-lg shadow-xl p-6 w-[560px] max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold">Sync History</h2>
              <button onClick={() => setShowHistory(false)} className="text-gray-400 hover:text-gray-600 text-xl">&times;</button>
            </div>
            {syncHistory.length === 0 ? (
              <p className="text-sm text-gray-500" data-testid="history-empty">No syncs recorded yet for this view.</p>
            ) : (
              <div className="overflow-y-auto flex-1">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left border-b">
                      <th className="pb-2 font-medium">When</th>
                      <th className="pb-2 font-medium">Changes</th>
                      <th className="pb-2 font-medium">Success</th>
                      <th className="pb-2 font-medium">Failed</th>
                      <th className="pb-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {syncHistory.map(entry => (
                      <tr key={entry.id} className="border-b last:border-b-0 hover:bg-gray-50" data-testid={`history-entry-${entry.id}`}>
                        <td className="py-2 text-gray-600">
                          {new Date(entry.synced_at).toLocaleString()}
                        </td>
                        <td className="py-2">{entry.total_changes}</td>
                        <td className="py-2 text-green-700">{entry.success_count}</td>
                        <td className="py-2 text-red-700">
                          {entry.failure_count > 0 ? entry.failure_count : '—'}
                        </td>
                        <td className="py-2">
                          {entry.success_count > 0 && (
                            <button
                              onClick={async () => {
                                if (!confirm(`Revert this sync? This will undo ${entry.success_count} change(s) in Salesforce.`)) return;
                                setRevertingId(entry.id);
                                setRevertConflicts(null);
                                setRevertConflictEntryId(null);
                                try {
                                  const result = await api.revertSync(viewId, entry.id);
                                  if (result.conflicts && result.conflicts.length > 0) {
                                    // Show conflict panel — partial revert (skipped conflicted fields)
                                    setRevertConflicts(result.conflicts);
                                    setRevertConflictEntryId(entry.id);
                                  } else {
                                    // Clean revert — no conflicts
                                    setRevertConflicts(null);
                                    setRevertConflictEntryId(null);
                                  }
                                  // Refresh history list to show the new revert entry
                                  try {
                                    const history = await api.fetchSyncHistory(viewId);
                                    setSyncHistory(history);
                                  } catch (_) {}
                                } catch (e) {
                                  alert('Revert failed: ' + e.message);
                                } finally {
                                  setRevertingId(null);
                                }
                              }}
                              disabled={revertingId === entry.id}
                              className="px-2 py-0.5 text-xs border border-amber-400 text-amber-700 rounded hover:bg-amber-50 disabled:opacity-50"
                              data-testid={`revert-btn-${entry.id}`}
                            >
                              {revertingId === entry.id ? '…' : 'Revert'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
            {/* Amendment E: Conflict panel */}
            {revertConflicts && revertConflicts.length > 0 && (
              <div className="mt-4 border border-yellow-400 rounded p-3 bg-yellow-50" data-testid="revert-conflicts">
                <div className="font-medium text-yellow-800 mb-2">
                  <span data-testid="conflict-count">{revertConflicts.length}</span> field(s) were modified externally since the original sync and were skipped.
                </div>
                <div className="space-y-1 max-h-32 overflow-y-auto text-xs font-mono mb-2">
                  {revertConflicts.map((c, i) => (
                    <div key={i} className="flex gap-2 text-gray-700" data-testid="revert-conflict-item">
                      <span className="font-semibold">{c.object}.{c.field}:</span>
                      <span>current <strong>{String(c.current_value ?? 'null')}</strong></span>
                      <span className="text-gray-400">(expected {String(c.expected_value ?? 'null')})</span>
                    </div>
                  ))}
                </div>
                <div className="flex gap-2">
                  <button
                    className="px-3 py-1 text-xs bg-yellow-600 text-white rounded hover:bg-yellow-700 disabled:opacity-50"
                    data-testid="force-revert-btn"
                    disabled={!!revertingId}
                    onClick={async () => {
                      if (!confirm('Force revert will overwrite external changes. Are you sure?')) return;
                      const entryId = revertConflictEntryId;
                      setRevertingId(entryId);
                      try {
                        await api.revertSync(viewId, entryId, true);
                        setRevertConflicts(null);
                        setRevertConflictEntryId(null);
                        try {
                          const history = await api.fetchSyncHistory(viewId);
                          setSyncHistory(history);
                        } catch (_) {}
                      } catch (e) {
                        alert('Force revert failed: ' + e.message);
                      } finally {
                        setRevertingId(null);
                      }
                    }}
                  >
                    Force Revert Anyway
                  </button>
                  <button
                    className="px-3 py-1 text-xs bg-gray-200 rounded hover:bg-gray-300"
                    data-testid="dismiss-conflicts-btn"
                    onClick={() => { setRevertConflicts(null); setRevertConflictEntryId(null); }}
                  >
                    Dismiss
                  </button>
                </div>
              </div>
            )}
            <div className="flex justify-end mt-4">
              <button onClick={() => setShowHistory(false)} className="px-3 py-1 border rounded" data-testid="close-history-btn">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* SOQL Editor Modal — convert free sheet to data mode */}
      {showSOQLEditorModal && (
        <SOQLEditorModal viewId={viewId}
          onClose={() => setShowSOQLEditorModal(false)}
          onSave={() => { setShowSOQLEditorModal(false); loadData(view); }} />
      )}

      {/* Add Column Dialog */}
      {showAddColumnDialog && (
        <AddColumnDialog
          columnLineage={columnLineage}
          viewId={viewId}
          onClose={() => setShowAddColumnDialog(false)}
          onAddType1={handleAddType1Column}
          onAddType2={handleAddType2Column}
          onAddType3={handleAddType3Column}
        />
      )}

      {/* Range View Dialog */}
      {showRangeViewDialog && (
        <RangeViewDialog
          rangeViews={rangeViews}
          onAdd={(rv) => setRangeViews(prev => [...prev, { id: crypto.randomUUID(), ...rv }])}
          onDelete={(id) => setRangeViews(prev => prev.filter(rv => rv.id !== id))}
          onPromote={async (rv) => {
            // Extract cell data from the workbook for the range coordinates
            const celldata = sheetData?.[0]?.celldata || [];
            const headers = [];
            for (let c = rv.startCol; c <= rv.endCol; c++) {
              const headerCell = celldata.find(cell => cell.r === rv.startRow && cell.c === c);
              headers.push(headerCell?.v?.v != null ? String(headerCell.v.v) : `col_${c}`);
            }
            const rows = [];
            for (let r = rv.startRow + 1; r <= rv.endRow; r++) {
              const row = [];
              for (let c = rv.startCol; c <= rv.endCol; c++) {
                const cell = celldata.find(cell => cell.r === r && cell.c === c);
                row.push(cell?.v?.v ?? null);
              }
              rows.push(row);
            }
            try {
              await api.promoteRangeToDataSource(viewId, rv.name, headers, rows);
              // Mark as promoted in local state
              setRangeViews(prev => prev.map(r =>
                r.id === rv.id ? { ...r, is_promoted: true } : r
              ));
              alert(`range_${rv.name} is now available as a table in FrankenQL queries`);
            } catch (err) {
              alert('Failed to promote range: ' + err.message);
            }
          }}
          onClose={() => setShowRangeViewDialog(false)}
        />
      )}
    </div>
  );
}

// Add Column Dialog — Type 1 (Data Source) + Type 2 (New SF Field) + Type 3 (Spreadsheet Only)
function AddColumnDialog({ columnLineage, viewId, onClose, onAddType1, onAddType2, onAddType3 }) {
  const [type, setType] = useState(null);

  // Type 3 state
  const [t3Name, setT3Name] = useState('');
  const [t3ColType, setT3ColType] = useState('text');
  const [t3Formula, setT3Formula] = useState('');

  // Type 1 state
  const [t1Object, setT1Object] = useState('');
  const [t1Fields, setT1Fields] = useState([]);
  const [t1LoadingFields, setT1LoadingFields] = useState(false);
  const [t1SelectedField, setT1SelectedField] = useState(null);
  const [t1IsAdding, setT1IsAdding] = useState(false);

  // Type 2 state
  const [t2Object, setT2Object] = useState('');
  const [t2FieldName, setT2FieldName] = useState('');
  const [t2Label, setT2Label] = useState('');
  const [t2FieldType, setT2FieldType] = useState('Text');
  const [t2Length, setT2Length] = useState('');
  const [t2Required, setT2Required] = useState(false);
  const [t2PicklistValues, setT2PicklistValues] = useState('');
  const [t2IsCreating, setT2IsCreating] = useState(false);
  const [t2Error, setT2Error] = useState(null);
  const [t2CreatedField, setT2CreatedField] = useState(null); // field_api_name after success

  // Unique non-null objects from column lineage
  const availableObjects = [...new Set(
    columnLineage.filter(c => c.object && !c.is_range).map(c => c.object)
  )];

  // Fields already in the view (by field API name per object)
  const existingFields = new Set(
    columnLineage.filter(c => c.object && c.field && !c.is_range).map(c => `${c.object}:${c.field}`)
  );

  // Fetch fields for selected object
  useEffect(() => {
    if (!t1Object) { setT1Fields([]); setT1SelectedField(null); return; }
    setT1LoadingFields(true);
    setT1SelectedField(null);
    api.fetchFieldsByObject(t1Object)
      .then(data => {
        // fetchFieldsByObject returns { fields: [...] }
        const fields = (data.fields || data || []).filter(f => {
          // Exclude fields already in the view
          return !existingFields.has(`${t1Object}:${f.name || f.api_name}`);
        });
        setT1Fields(fields);
      })
      .catch(() => setT1Fields([]))
      .finally(() => setT1LoadingFields(false));
  }, [t1Object]);

  const handleAddType1 = async () => {
    if (!t1Object || !t1SelectedField) return;
    const fieldApiName = t1SelectedField.name || t1SelectedField.api_name;
    setT1IsAdding(true);
    try {
      await onAddType1(t1Object, fieldApiName);
      onClose();
    } catch (_) {
      // error shown by parent
    } finally {
      setT1IsAdding(false);
    }
  };

  const handleAddType2 = async () => {
    if (!t2Object || !t2FieldName.trim() || !t2Label.trim() || !t2FieldType) return;
    setT2Error(null);
    setT2IsCreating(true);
    try {
      const fieldDef = {
        field_name: t2FieldName.trim(),
        label: t2Label.trim(),
        field_type: t2FieldType,
        required: t2Required,
      };
      if (t2Length) fieldDef.length = parseInt(t2Length, 10);
      if (t2FieldType === 'Picklist' && t2PicklistValues.trim()) {
        fieldDef.picklist_values = t2PicklistValues.split('\n').map(v => v.trim()).filter(Boolean);
      }
      const result = await onAddType2(t2Object, fieldDef);
      setT2CreatedField(result.field_api_name);
    } catch (err) {
      setT2Error(err.message || 'Failed to create field');
    } finally {
      setT2IsCreating(false);
    }
  };

  const handleAddType3 = () => {
    if (!t3Name.trim()) return;
    onAddType3({ name: t3Name.trim(), type: t3ColType, formula: t3Formula });
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" data-testid="add-column-dialog">
      <div className="bg-white rounded-lg shadow-xl p-6 w-[480px]">
        <h2 className="text-lg font-semibold mb-4">Add Column</h2>

        {/* Column type picker */}
        {!type && (
          <>
            <div className="space-y-3">
              {availableObjects.length > 0 && (
                <button
                  onClick={() => setType('type1')}
                  className="w-full text-left p-3 border rounded hover:bg-blue-50"
                  data-testid="add-type1-btn"
                >
                  <div className="font-medium">Data Source Field</div>
                  <div className="text-sm text-gray-500">
                    Add an existing field from {availableObjects.join(', ')} — syncs to Salesforce
                  </div>
                </button>
              )}
              {availableObjects.length > 0 && (
                <button
                  onClick={() => { setT2Object(availableObjects[0] || ''); setType('type2'); }}
                  className="w-full text-left p-3 border rounded hover:bg-orange-50"
                  data-testid="add-type2-btn"
                >
                  <div className="font-medium text-orange-700">New Salesforce Field</div>
                  <div className="text-sm text-gray-500">
                    Create a permanent custom field on {availableObjects[0]}{availableObjects.length > 1 ? ' or other objects' : ''} — irreversible schema change
                  </div>
                </button>
              )}
              <button
                onClick={() => setType('type3')}
                className="w-full text-left p-3 border rounded hover:bg-blue-50"
                data-testid="add-type3-btn"
              >
                <div className="font-medium">Spreadsheet Column</div>
                <div className="text-sm text-gray-500">Formula or static column, never synced to Salesforce</div>
              </button>
            </div>
            <div className="flex justify-end mt-4">
              <button onClick={onClose} className="px-3 py-1 border rounded">Cancel</button>
            </div>
          </>
        )}

        {/* Type 1 — Data Source Field picker */}
        {type === 'type1' && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">Object</label>
              <select
                className="w-full border rounded px-2 py-1"
                value={t1Object}
                onChange={e => setT1Object(e.target.value)}
                data-testid="t1-object-select"
              >
                <option value="">Select object…</option>
                {availableObjects.map(obj => (
                  <option key={obj} value={obj}>{obj}</option>
                ))}
              </select>
            </div>

            {t1Object && (
              <div>
                <label className="block text-sm font-medium mb-1">
                  Field {t1LoadingFields && <span className="text-gray-400 font-normal">(loading…)</span>}
                </label>
                {!t1LoadingFields && t1Fields.length === 0 && (
                  <p className="text-sm text-gray-500">No additional fields available for {t1Object}.</p>
                )}
                {!t1LoadingFields && t1Fields.length > 0 && (
                  <div
                    className="border rounded max-h-48 overflow-y-auto"
                    data-testid="t1-field-list"
                  >
                    {t1Fields.map(f => {
                      const name = f.name || f.api_name;
                      const label = f.label || name;
                      const ftype = f.type || '';
                      const isSelected = t1SelectedField === f;
                      return (
                        <button
                          key={name}
                          className={`w-full text-left px-3 py-2 text-sm border-b last:border-b-0 hover:bg-blue-50 flex justify-between items-center ${
                            isSelected ? 'bg-blue-100' : ''
                          }`}
                          onClick={() => setT1SelectedField(f)}
                          data-testid={`t1-field-${name}`}
                        >
                          <span className="font-medium">{label}</span>
                          <span className="text-gray-400 text-xs ml-2">{ftype}</span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {t1SelectedField && (
              <div className="text-sm text-gray-600 bg-gray-50 rounded p-2">
                Adding <strong>{t1SelectedField.label || t1SelectedField.name}</strong> ({t1SelectedField.name || t1SelectedField.api_name}) from <strong>{t1Object}</strong>
                {' — '}will update the view SOQL and reload data.
              </div>
            )}

            <div className="flex gap-2 justify-end mt-4">
              <button onClick={() => setType(null)} className="px-3 py-1 border rounded">Back</button>
              <button
                onClick={handleAddType1}
                disabled={!t1SelectedField || t1IsAdding}
                className="px-3 py-1 bg-blue-600 text-white rounded disabled:opacity-50"
                data-testid="t1-confirm-btn"
              >
                {t1IsAdding ? 'Adding…' : 'Add Field'}
              </button>
            </div>
          </div>
        )}

        {/* Type 2 — New Salesforce Field (schema change) */}
        {type === 'type2' && (
          <div className="space-y-3">
            <div className="bg-orange-50 border border-orange-200 rounded p-3 text-sm text-orange-800">
              <strong>Warning:</strong> This will permanently add a custom field to your Salesforce object.
              Custom fields cannot be easily deleted once data is written to them. Make sure the field name and type are correct.
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Object</label>
              <select
                className="w-full border rounded px-2 py-1"
                value={t2Object}
                onChange={e => setT2Object(e.target.value)}
                data-testid="t2-object-select"
              >
                {availableObjects.map(obj => (
                  <option key={obj} value={obj}>{obj}</option>
                ))}
              </select>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-sm font-medium mb-1">Field API Name</label>
                <div className="flex items-center gap-1">
                  <input
                    className="w-full border rounded px-2 py-1 font-mono text-sm"
                    value={t2FieldName}
                    onChange={e => setT2FieldName(e.target.value.replace(/[^A-Za-z0-9_]/g, ''))}
                    placeholder="My_Field"
                    data-testid="t2-field-name-input"
                  />
                  <span className="text-gray-400 text-xs whitespace-nowrap">__c</span>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Label</label>
                <input
                  className="w-full border rounded px-2 py-1"
                  value={t2Label}
                  onChange={e => setT2Label(e.target.value)}
                  placeholder="My Field"
                  data-testid="t2-label-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-sm font-medium mb-1">Type</label>
                <select
                  className="w-full border rounded px-2 py-1"
                  value={t2FieldType}
                  onChange={e => setT2FieldType(e.target.value)}
                  data-testid="t2-field-type-select"
                >
                  <option value="Text">Text</option>
                  <option value="Number">Number</option>
                  <option value="Currency">Currency</option>
                  <option value="Percent">Percent</option>
                  <option value="Date">Date</option>
                  <option value="DateTime">Date/Time</option>
                  <option value="Checkbox">Checkbox</option>
                  <option value="Picklist">Picklist</option>
                  <option value="TextArea">Text Area</option>
                </select>
              </div>
              {(t2FieldType === 'Text' || t2FieldType === 'TextArea') && (
                <div>
                  <label className="block text-sm font-medium mb-1">Max Length</label>
                  <input
                    type="number"
                    className="w-full border rounded px-2 py-1"
                    value={t2Length}
                    onChange={e => setT2Length(e.target.value)}
                    placeholder="255"
                    min="1"
                    max={t2FieldType === 'TextArea' ? '255' : '255'}
                    data-testid="t2-length-input"
                  />
                </div>
              )}
            </div>

            {t2FieldType === 'Picklist' && (
              <div>
                <label className="block text-sm font-medium mb-1">
                  Picklist Values <span className="font-normal text-gray-500">(one per line)</span>
                </label>
                <textarea
                  className="w-full border rounded px-2 py-1 text-sm font-mono"
                  rows={4}
                  value={t2PicklistValues}
                  onChange={e => setT2PicklistValues(e.target.value)}
                  placeholder={"Active\nInactive\nPending"}
                  data-testid="t2-picklist-values-input"
                />
              </div>
            )}

            {t2FieldType !== 'Checkbox' && (
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={t2Required}
                  onChange={e => setT2Required(e.target.checked)}
                  data-testid="t2-required-checkbox"
                />
                Required field
              </label>
            )}

            {t2Error && (
              <div className="text-red-600 text-sm bg-red-50 border border-red-200 rounded p-2">{t2Error}</div>
            )}

            {t2CreatedField ? (
              <div className="space-y-3">
                <div className="bg-green-50 border border-green-200 rounded p-3 text-sm text-green-800">
                  <strong>Field created!</strong> <code>{t2CreatedField}</code> is now in Salesforce.<br />
                  Run a <strong>Data Sync</strong> from the Sync tab, then use <strong>Add Column → Data Source Field</strong> to add it to this view.
                </div>
                <div className="flex gap-2 justify-end mt-4">
                  <button onClick={onClose} className="px-3 py-1 bg-green-600 text-white rounded">Done</button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2 justify-end mt-4">
                <button onClick={() => setType(null)} className="px-3 py-1 border rounded">Back</button>
                <button
                  onClick={handleAddType2}
                  disabled={!t2Object || !t2FieldName.trim() || !t2Label.trim() || t2IsCreating}
                  className="px-3 py-1 bg-orange-600 text-white rounded disabled:opacity-50"
                  data-testid="t2-confirm-btn"
                >
                  {t2IsCreating ? 'Creating in Salesforce…' : 'Create Field'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Type 3 — Spreadsheet Only */}
        {type === 'type3' && (
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">Column Name</label>
              <input
                className="w-full border rounded px-2 py-1"
                value={t3Name}
                onChange={e => setT3Name(e.target.value)}
                placeholder="e.g., Revenue Score"
                data-testid="column-name-input"
                autoFocus
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Type</label>
              <select
                className="w-full border rounded px-2 py-1"
                value={t3ColType}
                onChange={e => setT3ColType(e.target.value)}
                data-testid="column-type-select"
              >
                <option value="text">Text</option>
                <option value="number">Number</option>
                <option value="formula">Formula</option>
              </select>
            </div>
            {t3ColType === 'formula' && (
              <div>
                <label className="block text-sm font-medium mb-1">Formula (Excel syntax)</label>
                <input
                  className="w-full border rounded px-2 py-1 font-mono"
                  value={t3Formula}
                  onChange={e => setT3Formula(e.target.value)}
                  placeholder="=A2*2"
                  data-testid="column-formula-input"
                />
              </div>
            )}
            <div className="flex gap-2 justify-end mt-4">
              <button onClick={() => setType(null)} className="px-3 py-1 border rounded">Back</button>
              <button
                onClick={handleAddType3}
                className="px-3 py-1 bg-blue-600 text-white rounded"
                data-testid="column-confirm-btn"
              >
                Add Column
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Range View Dialog — Define and manage named range views
function RangeViewDialog({ rangeViews, onAdd, onDelete, onPromote, onClose }) {
  const [name, setName] = useState('');
  const [rangeRef, setRangeRef] = useState('');
  const [error, setError] = useState(null);
  const [promoting, setPromoting] = useState(null); // range id being promoted

  const handleAdd = () => {
    if (!name.trim()) { setError('Name is required'); return; }
    if (!/^[A-Za-z0-9_]+$/.test(name.trim())) { setError('Name must be alphanumeric + underscore only'); return; }
    const parsed = parseRangeRef(rangeRef);
    if (!parsed) { setError('Invalid range reference (e.g. B2:D10)'); return; }
    if (parsed.startRow > parsed.endRow || parsed.startCol > parsed.endCol) {
      setError('Start must be before end (e.g. B2:D10, not D10:B2)'); return;
    }
    if (rangeViews.some(rv => rv.name === name.trim())) { setError('A range with this name already exists'); return; }
    onAdd({ name: name.trim(), ...parsed });
    setName('');
    setRangeRef('');
    setError(null);
  };

  return (
    <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" data-testid="range-view-dialog">
      <div className="bg-white rounded-lg shadow-xl p-6 w-[480px]">
        <h2 className="text-lg font-semibold mb-4">Range Views</h2>

        {/* Existing range views */}
        {rangeViews.length > 0 && (
          <div className="mb-4">
            <div className="text-sm font-medium text-gray-600 mb-2">Defined Ranges</div>
            <div className="border rounded divide-y max-h-40 overflow-y-auto">
              {rangeViews.map((rv, idx) => (
                <div key={rv.id} className="flex items-center justify-between px-3 py-2 text-sm">
                  <div className="flex items-center gap-2">
                    <span
                      className="inline-block w-3 h-3 rounded"
                      style={{ background: RANGE_COLORS[idx % RANGE_COLORS.length] }}
                    />
                    <span className="font-medium">{rv.name}</span>
                    <span className="text-gray-400 font-mono text-xs">
                      {idxToCol(rv.startCol)}{rv.startRow + 1}:{idxToCol(rv.endCol)}{rv.endRow + 1}
                    </span>
                    {rv.is_promoted && (
                      <span className="text-xs bg-green-100 text-green-700 px-1.5 py-0.5 rounded font-medium" data-testid={`range-promoted-badge-${rv.id}`}>
                        Data Source
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {onPromote && (
                      <button
                        onClick={async () => {
                          setPromoting(rv.id);
                          try {
                            await onPromote(rv);
                          } finally {
                            setPromoting(null);
                          }
                        }}
                        disabled={promoting === rv.id}
                        className="text-indigo-500 hover:text-indigo-700 text-xs font-medium"
                        data-testid={`promote-range-${rv.id}`}
                      >
                        {promoting === rv.id ? 'Promoting...' : rv.is_promoted ? 'Re-promote' : 'Promote to Data Source'}
                      </button>
                    )}
                    <button
                      onClick={() => onDelete(rv.id)}
                      className="text-red-400 hover:text-red-600 text-xs"
                      data-testid={`delete-range-${rv.id}`}
                    >
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Add new range view */}
        <div className="space-y-3">
          <div>
            <label className="block text-sm font-medium mb-1">Range Name</label>
            <input
              className="w-full border rounded px-2 py-1"
              value={name}
              onChange={e => { setName(e.target.value); setError(null); }}
              placeholder="e.g. QuarterlyBudget"
              data-testid="range-name-input"
              autoFocus
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Range Reference</label>
            <input
              className="w-full border rounded px-2 py-1 font-mono"
              value={rangeRef}
              onChange={e => { setRangeRef(e.target.value.toUpperCase()); setError(null); }}
              placeholder="e.g. B2:D10"
              data-testid="range-ref-input"
            />
          </div>
          {error && <div className="text-red-600 text-sm" data-testid="range-error">{error}</div>}
        </div>

        <div className="flex gap-2 justify-end mt-4">
          <button onClick={onClose} className="px-3 py-1 border rounded">Close</button>
          <button
            onClick={handleAdd}
            disabled={!name.trim() || !rangeRef.trim()}
            className="px-3 py-1 bg-indigo-600 text-white rounded disabled:opacity-50"
            data-testid="range-add-btn"
          >
            Add Range View
          </button>
        </div>

        <p className="text-xs text-gray-400 mt-3">
          Range views are saved with "Save Layout". Borders appear after save + reload.
        </p>
      </div>
    </div>
  );
}
